import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { Notification, NotificationCategory } from '../types/Notification';
import {
  BellIcon,
  CheckIcon,
  ArchiveBoxIcon,
  TrashIcon,
  FunnelIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  PhoneIcon,
  DocumentIcon,
  DocumentTextIcon,
  CogIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import Layout from '../components/Layout';
import notificationApi from '../services/notificationApi';

type CategoryFilter = 'all' | NotificationCategory;
type ReadFilter = 'all' | 'unread' | 'read';

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    isLoading,
    hasMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    loadMore,
    setSelectedCategory,
    setShowUnreadOnly,
  } = useNotifications();

  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [selectedNotifications, setSelectedNotifications] = useState<Set<number>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    setSelectedCategory(categoryFilter === 'all' ? null : categoryFilter);
  }, [categoryFilter, setSelectedCategory]);

  useEffect(() => {
    setShowUnreadOnly(readFilter === 'unread');
  }, [readFilter, setShowUnreadOnly]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'leads':
        return UserGroupIcon;
      case 'tasks':
        return ClipboardDocumentListIcon;
      case 'calls':
        return PhoneIcon;
      case 'documents':
        return DocumentIcon;
      case 'forms':
        return DocumentTextIcon;
      case 'system':
        return CogIcon;
      case 'automation':
        return BoltIcon;
      default:
        return BellIcon;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - notificationDate.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return notificationDate.toLocaleDateString();
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const toggleSelectNotification = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNotifications(newSelected);
  };

  const selectAll = () => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.id)));
    }
  };

  const handleBulkMarkRead = async () => {
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedNotifications);
      for (const id of ids) {
        await markAsRead(id);
      }
      setSelectedNotifications(new Set());
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedNotifications);
      for (const id of ids) {
        await archiveNotification(id);
      }
      setSelectedNotifications(new Set());
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the selected notifications?')) {
      return;
    }
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedNotifications);
      for (const id of ids) {
        await notificationApi.deleteNotification(id);
      }
      setSelectedNotifications(new Set());
      await fetchNotifications({ page: 1 });
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead(categoryFilter === 'all' ? undefined : categoryFilter);
  };

  const categoryTabs: { key: CategoryFilter; label: string; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { key: 'all', label: 'All', icon: BellIcon },
    { key: 'leads', label: 'Leads', icon: UserGroupIcon },
    { key: 'tasks', label: 'Tasks', icon: ClipboardDocumentListIcon },
    { key: 'calls', label: 'Calls', icon: PhoneIcon },
    { key: 'documents', label: 'Documents', icon: DocumentIcon },
    { key: 'forms', label: 'Forms', icon: DocumentTextIcon },
    { key: 'system', label: 'System', icon: CogIcon },
    { key: 'automation', label: 'Automation', icon: BoltIcon },
  ];

  return (
    <Layout title="Notifications" subtitle="Stay updated with your leads, tasks, and more">
      <div className="max-w-4xl mx-auto">
        {/* Filters and Actions Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          {/* Category Tabs */}
          <div className="flex items-center border-b border-gray-200 px-4 overflow-x-auto">
            {categoryTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setCategoryFilter(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  categoryFilter === tab.key
                    ? 'border-gray-800 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Actions Bar */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              {/* Read Filter */}
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-4 w-4 text-gray-400" />
                <select
                  value={readFilter}
                  onChange={(e) => setReadFilter(e.target.value as ReadFilter)}
                  className="text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>

              {/* Select All */}
              <button
                onClick={selectAll}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {selectedNotifications.size === notifications.length && notifications.length > 0
                  ? 'Deselect all'
                  : 'Select all'}
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Bulk Actions */}
              {selectedNotifications.size > 0 && (
                <>
                  <span className="text-sm text-gray-500">
                    {selectedNotifications.size} selected
                  </span>
                  <button
                    onClick={handleBulkMarkRead}
                    disabled={bulkActionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4" />
                    Mark Read
                  </button>
                  <button
                    onClick={handleBulkArchive}
                    disabled={bulkActionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    <ArchiveBoxIcon className="h-4 w-4" />
                    Archive
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}

              {/* Mark All Read */}
              {selectedNotifications.size === 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  <CheckIcon className="h-4 w-4" />
                  Mark all as read
                </button>
              )}

              {/* Settings Link */}
              <button
                onClick={() => navigate('/notifications/settings')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md hover:bg-gray-700"
              >
                <CogIcon className="h-4 w-4" />
                Settings
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <BellIcon className="h-12 w-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const CategoryIcon = getCategoryIcon(notification.category);
                const isSelected = selectedNotifications.has(notification.id);

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`flex items-start gap-4 p-4 cursor-pointer transition-colors ${
                      notification.isRead
                        ? 'bg-white hover:bg-gray-50'
                        : 'bg-blue-50/50 hover:bg-blue-50'
                    } ${isSelected ? 'ring-2 ring-inset ring-gray-400' : ''}`}
                  >
                    {/* Checkbox */}
                    <div
                      onClick={(e) => toggleSelectNotification(notification.id, e)}
                      className={`flex-shrink-0 w-5 h-5 rounded border cursor-pointer ${
                        isSelected
                          ? 'bg-gray-800 border-gray-800'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {isSelected && (
                        <CheckIcon className="h-5 w-5 text-white" />
                      )}
                    </div>

                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                      notification.isRead ? 'bg-gray-100' : 'bg-gray-800'
                    }`}>
                      {notification.isRead ? (
                        <CategoryIcon className="h-5 w-5 text-gray-600" />
                      ) : (
                        <BellAlertIcon className="h-5 w-5 text-white" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h4 className={`text-sm ${notification.isRead ? 'font-normal text-gray-900' : 'font-semibold text-gray-900'}`}>
                            {notification.title}
                          </h4>
                          <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </span>
                        </div>
                      </div>

                      {/* Meta info */}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                          <CategoryIcon className="h-3 w-3" />
                          {notification.category}
                        </span>
                        {notification.actionUrl && (
                          <span className="text-xs text-gray-800 font-medium">
                            View details →
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex-shrink-0 flex items-center gap-1">
                      {!notification.isRead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Mark as read"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveNotification(notification.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        title="Archive"
                      >
                        <ArchiveBoxIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={loadMore}
                disabled={isLoading}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                {isLoading ? 'Loading...' : 'Load more notifications'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
